import { ConfirmModal } from '@cad-arc';
import { FC, useCallback } from 'react';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { useNewConversationCancellationModalContext } from '../context/new-conversation-cancellation-modal-context';
import { ConversationKey } from '../models';
import { useMessagingStore } from './use-messaging-store';

export const useNewConversationCancellationModal = (
    setShowAllConversations?: (value: boolean) => void
) => {
    const {
        setSelectedConversationKey,
        setSelectedConversationFilter,
        setIsBookmarksView,
    } = useMessagingStore();
    const newConversationCancellationModalContextValue = useNewConversationCancellationModalContext();
    const { isFirstResponder } = useApplicationType();

    const handleCloseConfirmationModal = useCallback(() => {
        newConversationCancellationModalContextValue?.setIsOpen?.(false);
    }, [newConversationCancellationModalContextValue]);

    const handleConfirmClick = useCallback(() => {
        newConversationCancellationModalContextValue?.setIsOpen?.(false);
        if (isFirstResponder && setShowAllConversations) {
            setShowAllConversations(true);
        }

        const confirmCancellationStoredData =
            newConversationCancellationModalContextValue?.confirmCancellationStoredData?.current;
        if (confirmCancellationStoredData instanceof ConversationKey) {
            // when click conversation
            setSelectedConversationKey(confirmCancellationStoredData);
        } else if (confirmCancellationStoredData === 'bookmarks-view') {
            // when click bookmarks button
            setIsBookmarksView(true);
            setSelectedConversationKey(undefined);
        } else if (confirmCancellationStoredData?.filterType) {
            // when click tab filters
            setSelectedConversationFilter(confirmCancellationStoredData);
            setSelectedConversationKey(undefined);
        } else {
            // when click close new conversation
            setSelectedConversationKey(undefined);
        }
    }, [
        newConversationCancellationModalContextValue,
        isFirstResponder,
        setShowAllConversations,
        setSelectedConversationKey,
        setSelectedConversationFilter,
        setIsBookmarksView,
    ]);

    const NewConversationCancellationModal: FC = () => (
        <ConfirmModal
            data-testid="new-conversation-cancellation-modal"
            isOpen={!!newConversationCancellationModalContextValue?.isOpen}
            title="Discard New Conversation"
            confirmLabel="Discard"
            cancelLabel="Continue Editing"
            onClose={handleCloseConfirmationModal}
            onEsc={handleCloseConfirmationModal}
            onOverlayClick={handleCloseConfirmationModal}
            body="Are you sure you want to discard creating a new conversation?"
            onConfirm={handleConfirmClick}
        />
    );

    return {
        isOpen: !!newConversationCancellationModalContextValue?.isOpen,
        setIsOpen: newConversationCancellationModalContextValue?.setIsOpen,
        confirmCancellationStoredData:
            newConversationCancellationModalContextValue?.confirmCancellationStoredData?.current,
        updateConfirmCancellationStoredData:
            newConversationCancellationModalContextValue?.updateConfirmCancellationStoredData,
        handleCloseConfirmationModal,
        handleConfirmClick,
        NewConversationCancellationModal,
    };
};
